/**
 * serverSave — the CLIENT half of server-synced saves.
 *
 * "The save IS the world", and IndexedDB is the local *cache*; the thin proxy's
 * flat-file store is the **durable source of truth**. This module reaches that
 * proxy (`GET`/`PUT /api/save`) with the same user-entered bearer the chat proxy
 * uses, parses the {@link SaveGetResponse} / {@link SavePutResponse} DTOs, and —
 * crucially — **never throws and never rejects** on a network/HTTP/parse error. A
 * server hiccup must never lose progress or crash a scene, so every failure path
 * resolves to `null` (get) or `false` (put). The local IndexedDB cache always
 * carries the world regardless.
 *
 * No secrets here: the bearer is the token the auth gate stored, passed in via
 * deps; this module never sees the API key.
 */
import type {
  Save,
  SaveGetResponse,
  SavePutRequest,
  SavePutResponse,
  SaveListResponse,
  SaveListEntry,
  SaveDeleteResponse,
} from '@/shared';
import { isSaveLike } from './validate.js';

/** The slot used when a caller doesn't name one (mirrors the server default). */
export const DEFAULT_SLOT = 'default';

/** The minimal wiring the server-save client needs to reach the proxy. */
export interface ServerSaveDeps {
  /** Proxy base URL (env-configurable; empty string ⇒ same-origin). */
  readonly apiBaseUrl?: string;
  /** Bearer token for the proxy (user-entered; null/absent ⇒ no sync). */
  readonly proxyToken?: string | null;
  /** Injectable fetch (tests). Defaults to the global fetch when present. */
  readonly fetchImpl?: typeof fetch;
}

/** Resolve a usable fetch, or null when none is available (e.g. a bare test env). */
function resolveFetch(deps: ServerSaveDeps): typeof fetch | null {
  if (deps.fetchImpl) return deps.fetchImpl;
  return typeof fetch === 'function' ? fetch : null;
}

/** Whether we can even attempt a sync: we need a fetch and a bearer token. */
function canSync(deps: ServerSaveDeps): boolean {
  return !!resolveFetch(deps) && !!(deps.proxyToken ?? null);
}

/** Trim trailing slashes so `${base}/api/...` never doubles up. */
function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/+$/, '')}${path}`;
}

/**
 * Fetch the durable server save for a slot. Returns the parsed
 * `{ save, updatedAt }` when the server holds one, or `null` when the slot is
 * empty, sync is unconfigured, or **anything** goes wrong (offline, 4xx/5xx,
 * malformed body, an un-save-like payload). Never throws / never rejects.
 */
export async function getServerSave(
  deps: ServerSaveDeps,
  slot: string = DEFAULT_SLOT,
): Promise<{ save: Save; updatedAt: number | null } | null> {
  if (!canSync(deps)) return null;
  const f = resolveFetch(deps)!;
  const base = deps.apiBaseUrl ?? '';
  const token = deps.proxyToken!;

  try {
    const url = joinUrl(base, `/api/save?slot=${encodeURIComponent(slot)}`);
    const res = await f(url, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;

    const body = (await res.json()) as SaveGetResponse;
    if (!body || typeof body !== 'object') return null;

    // No durable save yet for this slot.
    if (body.save == null) return null;

    // Re-validate through the same gate the IndexedDB load path uses: a server
    // payload is no more trusted than disk. Reject anything not save-like.
    if (!isSaveLike(body.save)) return null;

    const updatedAt = typeof body.updatedAt === 'number' ? body.updatedAt : null;
    return { save: body.save, updatedAt };
  } catch {
    return null;
  }
}

/**
 * Persist a save to the durable server store (last-write-wins). Resolves `true`
 * on a confirmed write, `false` on any failure (unconfigured, offline, non-2xx,
 * 413-too-large, malformed response). Never throws / never rejects — the caller
 * treats a `false` as "the IndexedDB cache still holds it, retry next save".
 */
export async function putServerSave(
  slot: string | undefined,
  save: Save,
  updatedAt: number,
  deps: ServerSaveDeps,
): Promise<boolean> {
  if (!canSync(deps)) return false;
  const f = resolveFetch(deps)!;
  const base = deps.apiBaseUrl ?? '';
  const token = deps.proxyToken!;

  const body: SavePutRequest = {
    ...(slot ? { slot } : {}),
    save,
    updatedAt,
  };

  try {
    const url = joinUrl(base, '/api/save');
    const res = await f(url, {
      method: 'PUT',
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) return false;

    // A 200 is enough; the echoed updatedAt is read defensively for bookkeeping.
    const parsed = (await res.json().catch(() => null)) as SavePutResponse | null;
    return parsed ? parsed.ok === true : true;
  } catch {
    return false;
  }
}

/**
 * List the slots the server holds (for the Continue picker). Returns lightweight
 * `{ slot, updatedAt }` metadata; pull a slot's full save via {@link getServerSave}
 * when its details are needed. Resolves `[]` when sync is unconfigured or **anything**
 * goes wrong (offline, 4xx/5xx, malformed body). Never throws / never rejects.
 */
export async function listServerSaves(deps: ServerSaveDeps): Promise<SaveListEntry[]> {
  if (!canSync(deps)) return [];
  const f = resolveFetch(deps)!;
  const base = deps.apiBaseUrl ?? '';
  const token = deps.proxyToken!;

  try {
    const url = joinUrl(base, '/api/saves');
    const res = await f(url, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) return [];

    const body = (await res.json()) as SaveListResponse;
    if (!body || typeof body !== 'object' || !Array.isArray(body.saves)) return [];

    // Defensive: keep only well-formed entries (a slot string + a numeric/null ts).
    return body.saves
      .filter((e): e is SaveListEntry => !!e && typeof e.slot === 'string' && e.slot.length > 0)
      .map((e) => ({
        slot: e.slot,
        updatedAt: typeof e.updatedAt === 'number' ? e.updatedAt : 0,
      }));
  } catch {
    return [];
  }
}

/**
 * Delete a slot on the server. Resolves `true` on a confirmed delete, `false` on
 * any failure (unconfigured, offline, non-2xx, malformed response). Idempotent
 * server-side: deleting an absent slot still confirms ok. Never throws / never
 * rejects.
 */
export async function deleteServerSave(slot: string, deps: ServerSaveDeps): Promise<boolean> {
  if (!canSync(deps)) return false;
  const f = resolveFetch(deps)!;
  const base = deps.apiBaseUrl ?? '';
  const token = deps.proxyToken!;

  try {
    const url = joinUrl(base, `/api/save?slot=${encodeURIComponent(slot)}`);
    const res = await f(url, {
      method: 'DELETE',
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) return false;

    const parsed = (await res.json().catch(() => null)) as SaveDeleteResponse | null;
    return parsed ? parsed.ok === true : true;
  } catch {
    return false;
  }
}

/**
 * A trailing-edge debouncer for best-effort puts: rapid `save()` calls collapse to
 * a single network write of the *latest* payload after `waitMs` of quiet. Each
 * call captures the freshest save, so an in-flight burst never ships stale data.
 *
 * The returned function never throws. `flush()` fires any pending write
 * immediately (e.g. on tab close); `cancel()` drops a pending write.
 */
export interface DebouncedPut {
  /** Queue a (debounced) durable write of `save` at `updatedAt`. */
  (save: Save, updatedAt: number): void;
  /** Fire any pending write now (returns the in-flight promise). */
  flush(): Promise<boolean>;
  /** Drop any pending write without firing it. */
  cancel(): void;
}

/** Build a debounced wrapper around `putServerSave` for one slot + deps. */
export function debouncePut(
  deps: ServerSaveDeps,
  slot: string = DEFAULT_SLOT,
  waitMs = 1500,
): DebouncedPut {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: { save: Save; updatedAt: number } | null = null;

  const fire = async (): Promise<boolean> => {
    const next = pending;
    pending = null;
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
    if (!next) return false;
    return putServerSave(slot, next.save, next.updatedAt, deps);
  };

  const debounced = ((save: Save, updatedAt: number): void => {
    pending = { save, updatedAt };
    if (timer != null) clearTimeout(timer);
    timer = setTimeout(() => {
      void fire();
    }, waitMs);
  }) as DebouncedPut;

  debounced.flush = fire;
  debounced.cancel = (): void => {
    pending = null;
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return debounced;
}
